// @ts-check

/**
 * @file Renders the timelogs dashboard billing progress card.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import {memo} from "react"
import {View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"

import Card from "../../../components/card"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogsDashboardBillingProgressCardProps
 * @property {string} description Supporting text below the progress bar.
 * @property {number} percent Billing progress percentage.
 * @property {string} testID Base test id for the card.
 * @property {string} title Progress card label.
 */

/** @typedef {Record<string, never>} TimelogsDashboardBillingProgressCardState */

/**
 * Displays invoiced progress as a compact progress bar card.
 * @augments {ShapeComponent<TimelogsDashboardBillingProgressCardProps, TimelogsDashboardBillingProgressCardState>}
 */
class TimelogsDashboardBillingProgressCard extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active dashboard theme into the progress card. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders billing progress details.
   * @returns {ReactNode} Billing progress card content.
   */
  render() {
    const {description, percent, testID, title} = this.p
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <Card
        style={styles.card ||= {
          flexGrow: 1,
          gap: 12,
          minWidth: 220,
          padding: 20
        }}
        testID={testID}
      >
        <View
          style={styles.header ||= {
            alignItems: "center",
            flexDirection: "row",
            justifyContent: "space-between"
          }}
          testID={`${testID}/header`}
        >
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: "uppercase"
            }}
            testID={`${testID}/title`}
          >
            {title}
          </Text>
          <Text
            style={styles[`percent-${themeName}`] ||= {
              color: colors.text,
              fontSize: 13,
              fontWeight: 700
            }}
            testID={`${testID}/percent`}
          >
            {percent}%
          </Text>
        </View>
        <View
          style={styles[`track-${themeName}`] ||= {
            backgroundColor: colors.surfaceSunken,
            borderRadius: 999,
            height: 12,
            overflow: "hidden"
          }}
          testID={`${testID}/track`}
        >
          <View
            style={styles[`fill-${themeName}-${percent}`] ||= {
              backgroundColor: colors.info,
              height: "100%",
              width: `${Math.max(0, Math.min(100, percent))}%`
            }}
            testID={`${testID}/fill`}
          />
        </View>
        <Text
          style={styles[`description-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 13,
            fontWeight: 600
          }}
          testID={`${testID}/description`}
        >
          {description}
        </Text>
      </Card>
    )
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardBillingProgressCardProps>} */
const TimelogsDashboardBillingProgressCardComponent = memo(shapeComponent(TimelogsDashboardBillingProgressCard))

export default TimelogsDashboardBillingProgressCardComponent
